import { clamp } from "lodash";
import React, { useCallback, useEffect, useRef } from "react";

import { IconSize, IconSizeTable } from "../icon";
import css from "./radial_bar.module.scss";

type RadialBarProps = {
  value?: number;
  size?: IconSize;
  strokeWidth?: number;
  maxValue?: number;
  minLength?: number;
};

export const RadialBar = ({
  value = 0,
  size = "md",
  strokeWidth = 6,
  maxValue = 100,
  minLength = 5
}: RadialBarProps) => {
  const animationTimeoutIdRef = useRef<NodeJS.Timeout>();
  const circleRef = useRef<SVGCircleElement>(null);

  const length = IconSizeTable[size];
  const radius = length / 2 - 3; // -3 handles line width
  const center = length / 2;
  const fullLength = radius * Math.PI * 2;

  const getOffset = useCallback(
    (value: number) => {
      const clampedValue = clamp(value, 0, 1);
      let offset = fullLength * (1 - clampedValue);
      offset = Math.min(offset, fullLength - minLength);

      return parseFloat(offset.toFixed(1));
    },
    [fullLength, minLength]
  );

  const animateValue = useCallback(() => {
    const valueOffset = getOffset(value / maxValue);

    animationTimeoutIdRef.current = setTimeout(() => {
      circleRef.current?.setAttribute("stroke-dashoffset", valueOffset.toString());
    }, 0);
  }, [value, maxValue, getOffset]);

  useEffect(() => {
    animateValue();
    return () => animationTimeoutIdRef.current && clearTimeout(animationTimeoutIdRef.current);
  }, [animateValue]);

  const sizeToPixels = (size: number) => `${size}px`;

  const lengthInPixels = sizeToPixels(length);

  return (
    <svg className={css.svg} height={lengthInPixels} width={lengthInPixels}>
      <circle
        className={css.background}
        r={sizeToPixels(radius)}
        cx={sizeToPixels(center)}
        cy={sizeToPixels(center)}
        fill="transparent"
        strokeDashoffset={getOffset(0)}
        strokeWidth={strokeWidth}
      />
      <circle
        ref={circleRef}
        className={css.circle}
        r={sizeToPixels(radius)}
        cx={sizeToPixels(center)}
        cy={sizeToPixels(center)}
        fill="transparent"
        strokeDasharray={sizeToPixels(fullLength)}
        strokeDashoffset={getOffset(0)}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
    </svg>
  );
};
